import React, { useCallback } from "react";
import formatSequencial from "@tvg/formatter/numeric";
import { get } from "lodash";
import { FavoriteRunners } from "@tvg/ts-types/Race";
import {
  AttentionContainer,
  Content,
  DetailAction,
  DetailsHr,
  LabelDot,
  LabelIconWrapper,
  LegParagraph,
  PickBetLabelIconContainer,
  PositionsContainer,
  RowWrapper,
  SaddleContainer,
  TextAndSelectionContainer,
  WillPaysInfoContainer,
  WillPaysLabel
} from "./styled-components";
import type { Selection } from "../../../SaddleSelections/types";
import { BetSelections } from "../../../BetSelections";
import { Paragraph } from "../../../Typography";
import { Icon } from "../../../Icon";
import { type CustomBetSelectionsViewProps } from "../../types";

export const CustomBetSelectionsView: React.FC<
  CustomBetSelectionsViewProps
> = ({
  completeSelection,
  numWagerableRunners,
  type,
  isKey,
  isBox,
  isWheel,
  isLeg,
  isExoticBet,
  currentRaceNumber,
  scratches,
  breed,
  wagerDetails,
  onDetailsView,
  racesStatus,
  isSpecialWager,
  enableMyBetsBehavior = false,
  favoriteRunnerByLeg,
  showReplacedRunners,
  showAlternatePlaceholder
}) => {
  const renderKeyOrKeyBox = (index: number) => {
    const isKeyBox = isKey && isBox;
    const defaultText = isKeyBox ? "Box" : "w/";

    return (
      <Paragraph
        qaLabel={`selection-label-key${isKeyBox ? "-box" : ""}`}
        color="--fd-colors-content-subtle"
        fontFamily="regular"
      >
        {index === 0 ? (
          <Icon
            name="key"
            size="s"
            qaLabel="key"
            lineColor="--fd-colors-content-subtle"
            fontFamily="regular"
          />
        ) : (
          defaultText
        )}
      </Paragraph>
    );
  };

  const renderKeyBoxOrKeyOrBox = (index: number) => {
    if (isKey) {
      return renderKeyOrKeyBox(index);
    }

    return (
      <Paragraph
        color="--fd-colors-content-subtle"
        qaLabel="selection-label-box"
        fontFamily="regular"
      >
        Box
      </Paragraph>
    );
  };

  const renderExoticOrLegBet = (index: number) => {
    const currentRaceStatus = get(racesStatus, index);

    return isExoticBet ? (
      <Paragraph
        qaLabel={`selection-label-${formatSequencial(index + 1)}`}
        color="--fd-colors-content-subtle"
        fontFamily="regular"
      >
        {formatSequencial(index + 1)}
      </Paragraph>
    ) : (
      <Content>
        <LegParagraph
          qaLabel={`selection-label-${formatSequencial(index + 1)}`}
          fontFamily="condensedRegular"
          ml="space-1"
          mr="space-3"
          raceStatus={currentRaceStatus}
        >
          {isSpecialWager
            ? `L${Number(currentRaceNumber) + index}`
            : `LEG ${index + 1} - R${Number(currentRaceNumber) + index}`}
        </LegParagraph>
      </Content>
    );
  };

  const renderWheel = (index: number) => (
    <Paragraph
      qaLabel="selection-label-wheel"
      color="--fd-colors-content-subtle"
      fontFamily="regular"
    >
      {index === 0 ? <></> : "w/"}
    </Paragraph>
  );

  const renderLegLabelIcon = (index: number) => {
    const currentRaceStatus = get(racesStatus, index);
    switch (currentRaceStatus) {
      case "attention":
        return (
          <PickBetLabelIconContainer>
            <LabelIconWrapper raceStatus="attention">
              <Icon
                qaLabel="attention-leg-icon"
                name="changes"
                size="s"
                lineColor="--fd-colors-system-warning-content-accent"
              />
            </LabelIconWrapper>
          </PickBetLabelIconContainer>
        );
      case "lost":
        return (
          <PickBetLabelIconContainer>
            <LabelIconWrapper raceStatus="lost">
              <Icon
                qaLabel="lost-leg-icon"
                name="error"
                size="s"
                lineColor="--fd-colors-system-important-background-default"
              />
            </LabelIconWrapper>
          </PickBetLabelIconContainer>
        );
      case "win":
        return (
          <PickBetLabelIconContainer>
            <LabelIconWrapper raceStatus="win">
              <Icon
                qaLabel="win-leg-icon"
                name="success"
                size="s"
                lineColor="--fd-colors-system-positive-background-default"
              />
            </LabelIconWrapper>
          </PickBetLabelIconContainer>
        );
      default:
        return (
          <PickBetLabelIconContainer>
            <LabelIconWrapper raceStatus={currentRaceStatus}>
              <LabelDot raceStatus={currentRaceStatus} />
            </LabelIconWrapper>
          </PickBetLabelIconContainer>
        );
    }
  };

  const replacementMessage = useCallback(
    (selections: Selection[], index: number) => {
      let message = "Awaiting official result to confirm the bet status.";
      if (
        scratches?.[index].length &&
        selections.some(
          (item) =>
            item.isAlternate && !scratches?.[index].includes(item.number)
        )
      ) {
        message = `Scratch will be replaced with the alternate selection. ${message}`;
      } else if (isLeg) {
        message = `Scratch will be replaced with the FAV. ${message}`;
      }
      return message;
    },
    [isLeg, scratches]
  );

  const renderLabelAndSelections = (
    selection: Selection[],
    index: number,
    hasWillPaysTag: boolean,
    showOdds: boolean,
    favRunnersByLeg: FavoriteRunners | null | undefined
  ) => {
    const currentRaceStatus = get(racesStatus, index);
    const favoriteRunner = favRunnersByLeg?.[index];

    return isLeg ? (
      <TextAndSelectionContainer
        hasDetails={get(wagerDetails, index, false)}
        isLeg={isLeg}
      >
        {(isKey || isBox) && renderKeyBoxOrKeyOrBox(index)}
        {(isExoticBet || isLeg) && renderExoticOrLegBet(index)}
        {isWheel && renderWheel(index)}
        {hasWillPaysTag && scratches?.[index].length !== selection.length && (
          <WillPaysInfoContainer>
            <LabelIconWrapper raceStatus="win">
              <Icon
                qaLabel="info"
                name="info"
                size="s"
                lineColor="--fd-colors-system-info-content-accent"
              />
            </LabelIconWrapper>
            Will Pays subject to change due to scratches
          </WillPaysInfoContainer>
        )}
        {!hasWillPaysTag &&
          currentRaceStatus === "unknown" &&
          isLeg &&
          get(wagerDetails, index, false) && (
            <AttentionContainer>
              Result for this leg currently pending.
            </AttentionContainer>
          )}

        {!hasWillPaysTag &&
          currentRaceStatus === "attention" &&
          isLeg &&
          get(wagerDetails, index, false) && (
            <AttentionContainer>
              {replacementMessage(selection, index)}
            </AttentionContainer>
          )}

        <SaddleContainer
          isLeg={isLeg}
          hasDetails={get(wagerDetails, index, false)}
        >
          <BetSelections
            scratches={scratches?.[index]}
            numWagerableRunners={
              isLeg ? numWagerableRunners?.[index] : numWagerableRunners?.[0]
            }
            selections={selection}
            type={type}
            showBetType={false}
            breed={breed}
            wagerDetails={get(wagerDetails, index, false)}
            hideWillPaysTag={!hasWillPaysTag}
            showOdds={showOdds}
            enableMyBetsBehavior={enableMyBetsBehavior}
            favoriteRunner={favoriteRunner as unknown as FavoriteRunners}
            showReplacedRunners={showReplacedRunners}
            raceStatus={currentRaceStatus}
            isDark={false}
            showAlternatePlaceholder={showAlternatePlaceholder}
          />
          {get(wagerDetails, index, false) &&
            completeSelection &&
            index !== (completeSelection?.length || 0) - 1 &&
            completeSelection?.length > 1 && <DetailsHr />}
        </SaddleContainer>
      </TextAndSelectionContainer>
    ) : (
      <>
        {(isKey || isBox) && renderKeyBoxOrKeyOrBox(index)}
        {(isExoticBet || isLeg) && renderExoticOrLegBet(index)}
        {isWheel && renderWheel(index)}
        <SaddleContainer
          isLeg={isLeg}
          hasDetails={get(wagerDetails, index, false)}
        >
          <BetSelections
            scratches={scratches?.[index]}
            numWagerableRunners={
              isLeg ? numWagerableRunners?.[index] : numWagerableRunners?.[0]
            }
            selections={selection}
            type={type}
            showBetType={false}
            breed={breed}
            wagerDetails={get(wagerDetails, index, false)}
            hideWillPaysTag={!hasWillPaysTag}
            enableMyBetsBehavior={enableMyBetsBehavior}
            favoriteRunner={favoriteRunner as unknown as FavoriteRunners}
            showReplacedRunners={showReplacedRunners}
            raceStatus={currentRaceStatus}
            isDark={false}
            showAlternatePlaceholder={showAlternatePlaceholder}
          />
          {get(wagerDetails, index, false) &&
            index !== (completeSelection?.length || 0) - 1 &&
            completeSelection &&
            completeSelection?.length > 1 && <DetailsHr />}
        </SaddleContainer>
      </>
    );
  };

  return completeSelection?.length ? (
    <>
      {completeSelection.map((selection, index) => {
        const willPaysExist = selection.some(
          (sel) => sel.willPay && sel.willPay > 0
        );

        const hasWillPaysTag =
          index === completeSelection.length - 1 &&
          willPaysExist &&
          scratches?.[index].length !== selection.length &&
          get(wagerDetails, index, false);

        const showOdds = get(wagerDetails, index, false);

        return (
          <PositionsContainer
            key={`${type}-${index.toString()}`}
            isLeg={isLeg}
            raceStatus={get(racesStatus, index)}
          >
            <RowWrapper isLeg={isLeg}>
              <DetailAction
                onClick={() => onDetailsView && onDetailsView(index)}
              />
              {isLeg && renderLegLabelIcon(index)}
              {renderLabelAndSelections(
                selection,
                index,
                hasWillPaysTag,
                showOdds,
                favoriteRunnerByLeg
              )}
              {hasWillPaysTag && <WillPaysLabel>WILL PAYS</WillPaysLabel>}
              {isLeg && wagerDetails && (
                <Icon
                  qaLabel={`${type}-${index}}-details-chevron`}
                  name={
                    get(wagerDetails, index, false)
                      ? "chevronUp"
                      : "chevronDown"
                  }
                  size="s"
                  lineColor="--fd-colors-content-subtle"
                />
              )}
            </RowWrapper>
          </PositionsContainer>
        );
      })}
    </>
  ) : null;
};
